import React, { useState, useEffect, useRef } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Search, X, UserPlus, UserMinus, RefreshCw, ArrowLeftRight } from 'lucide-react';

export default function QuickAssignPopover({
  isOpen,
  position, // { x, y } relative to canvas container
  tableInfo, // { tableId, tableName, seatIndex, currentGuest, isOccupied }
  unassignedGuests = [],
  assignedGuests = [], // guests assigned to OTHER tables (for swap)
  onAssign, // (guestId, tableId, seatIndex) => void
  onRemove, // (guestId) => void - remove guest from seat
  onSwap, // (guest1Id, guest2Id) => void - swap two guests
  onClose,
}) {
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState('assign'); // 'assign' or 'swap'
  const popoverRef = useRef(null);
  const inputRef = useRef(null);

  const isOccupied = tableInfo?.isOccupied;
  const currentGuest = tableInfo?.currentGuest;

  // Filter guests by search based on mode
  const filteredGuests = mode === 'swap' 
    ? assignedGuests.filter(guest =>
        guest.full_name.toLowerCase().includes(search.toLowerCase()) &&
        guest.id !== currentGuest?.id // Don't show current guest in swap list
      )
    : unassignedGuests.filter(guest =>
        guest.full_name.toLowerCase().includes(search.toLowerCase())
      );

  // Focus search input when popover opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
    // Reset search and mode when closed
    if (!isOpen) {
      setSearch('');
      setMode('assign');
    }
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleAssign = (guestId) => {
    if (mode === 'swap' && currentGuest && onSwap) {
      onSwap(currentGuest.id, guestId);
    } else {
      onAssign(guestId, tableInfo.tableId, tableInfo.seatIndex);
    }
    onClose();
  };

  const handleRemove = () => {
    if (currentGuest && onRemove) {
      onRemove(currentGuest.id);
      onClose();
    }
  };

  return (
    <div
      ref={popoverRef}
      className="absolute z-[100] animate-in fade-in-0 zoom-in-95 duration-150"
      style={{
        left: position.x,
        top: position.y,
        transform: 'translate(-50%, 0)',
      }}
      data-testid="quick-assign-popover"
    >
      <div className="bg-slate-900 border border-slate-700 rounded-lg shadow-2xl w-72 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700 bg-slate-800/50">
          <div className="flex items-center gap-2">
            {mode === 'swap' ? (
              <ArrowLeftRight className="h-4 w-4 text-[#D4AF37]" />
            ) : isOccupied ? (
              <RefreshCw className="h-4 w-4 text-[#D4AF37]" />
            ) : (
              <UserPlus className="h-4 w-4 text-[#D4AF37]" />
            )}
            <span className="text-xs font-medium text-white">
              {mode === 'swap' ? 'Swap Seats' : isOccupied ? 'Edit Seat' : 'Quick Assign'}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-5 w-5 p-0 text-slate-400 hover:text-white"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>

        {/* Table info */}
        <div className="px-3 py-2 bg-slate-800/30 border-b border-slate-700/50">
          <p className="text-[10px] text-slate-500 uppercase tracking-wide">
            {tableInfo.tableName}
          </p>
          {isOccupied && currentGuest && (
            <p className="text-sm text-[#D4AF37] font-medium truncate">
              {currentGuest.full_name}
            </p>
          )}
        </div>

        {/* Action buttons for occupied seats */}
        {isOccupied && currentGuest && mode === 'assign' && (
          <div className="p-2 border-b border-slate-700/50 flex gap-2">
            {onRemove && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRemove}
                className="flex-1 h-8 text-xs border-red-900/50 text-red-400 hover:bg-red-900/20 hover:text-red-300"
                data-testid="remove-guest-btn"
              >
                <UserMinus className="h-3.5 w-3.5 mr-1" />
                Remove
              </Button>
            )}
            {onSwap && assignedGuests.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setMode('swap')}
                className="flex-1 h-8 text-xs border-blue-900/50 text-blue-400 hover:bg-blue-900/20 hover:text-blue-300"
                data-testid="swap-guest-btn"
              >
                <ArrowLeftRight className="h-3.5 w-3.5 mr-1" />
                Swap
              </Button>
            )}
          </div>
        )}

        {/* Back button when in swap mode */}
        {mode === 'swap' && (
          <div className="p-2 border-b border-slate-700/50">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMode('assign')}
              className="w-full h-7 text-xs text-slate-400 hover:text-white"
            >
              ← Back to options
            </Button>
          </div>
        )}

        {/* Divider with label */}
        {isOccupied && mode === 'assign' && (
          <div className="px-3 py-1.5 bg-slate-800/20">
            <p className="text-[10px] text-slate-500 uppercase tracking-wide text-center">
              Or reassign to unassigned guest
            </p>
          </div>
        )}

        {mode === 'swap' && (
          <div className="px-3 py-1.5 bg-slate-800/20">
            <p className="text-[10px] text-slate-500 uppercase tracking-wide text-center">
              Select guest to swap with
            </p>
          </div>
        )}

        {/* Search */}
        <div className="p-2 border-b border-slate-700/50">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
            <Input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={mode === 'swap' ? "Search assigned guests..." : "Search guests..."}
              className="h-8 pl-7 text-xs bg-slate-800/50 border-slate-700"
              data-testid="quick-assign-search"
            />
          </div>
        </div>

        {/* Guest List */}
        <div className="max-h-48 overflow-auto">
          {filteredGuests.length === 0 ? (
            <div className="px-3 py-4 text-center">
              <p className="text-xs text-slate-500">
                {mode === 'swap'
                  ? 'No other assigned guests to swap with'
                  : unassignedGuests.length === 0 
                    ? `All guests are assigned (${assignedGuests?.length || 0} total)` 
                    : 'No guests match your search'}
              </p>
              {mode !== 'swap' && unassignedGuests.length === 0 && assignedGuests?.length > 0 && (
                <p className="text-[10px] text-slate-600 mt-2">
                  Use "Swap" to reassign or remove guests from other tables first
                </p>
              )}
            </div>
          ) : (
            <div className="py-1">
              {filteredGuests.slice(0, 20).map((guest) => (
                <button
                  key={guest.id}
                  onClick={() => handleAssign(guest.id)}
                  className="w-full px-3 py-2 text-left hover:bg-slate-800 transition-colors group"
                  data-testid={`quick-assign-guest-${guest.id}`}
                >
                  <p className="text-sm text-white group-hover:text-[#D4AF37] truncate">
                    {guest.full_name}
                  </p>
                  {guest.email && (
                    <p className="text-[10px] text-slate-500 truncate">
                      {guest.email}
                    </p>
                  )}
                </button>
              ))}
              {filteredGuests.length > 20 && (
                <p className="px-3 py-2 text-[10px] text-slate-500 text-center">
                  +{filteredGuests.length - 20} more guests...
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Arrow pointing up to seat */}
      <div 
        className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-slate-900 border-l border-t border-slate-700 rotate-45"
      />
    </div>
  );
}
